import React, { useId } from 'react'
import cn from 'classnames'
import { Check } from 'lucide-react'
import {
    aiModelBrandFor,
    aiModelBrandHaloFor,
    aiModelMonogramFor,
    aiModelMonogramTileFor,
    type AiModelBrandSubject,
} from 'state/aiModelBrands'
import styles from './ai-model-brand-avatar.module.scss'

interface AiModelBrandAvatarProps {
    model: AiModelBrandSubject | null | undefined
    // Pixel size. Omit to let the surrounding stylesheet size it via --ai-brand-avatar-size.
    size?: number
    className?: string
    // Shows a check badge on the tile's corner (model chooser selection state).
    selected?: boolean
    // Seats the tile on a soft disc of its brand hue (model chooser rows).
    halo?: boolean
}

// Brand tile for an AI action: the lab's mark on its brand colours, or a monogram
// when we have no mark for it. Purely decorative — the entry's text names the brand.
export default function AiModelBrandAvatar({
    model,
    size,
    className,
    selected = false,
    halo = false,
}: AiModelBrandAvatarProps) {
    const gradientId = `ai-brand-gradient-${useId().replace(/:/g, '')}`
    const brand = aiModelBrandFor(model)
    const mark = brand?.mark
    const tile = brand?.tile || aiModelMonogramTileFor(model)
    const monogram = brand?.monogram || aiModelMonogramFor(model)

    const style = {
        '--ai-brand-tile-bg': tile.background,
        '--ai-brand-tile-fg': tile.foreground,
        '--ai-brand-halo': aiModelBrandHaloFor(model),
        '--ai-brand-mark-scale': mark?.scale ?? 0.6,
        ...(size ? { '--ai-brand-avatar-size': `${size}px` } : {}),
    } as React.CSSProperties

    const avatar = (
        <span
            className={cn(styles.avatar, selected && styles.avatarSelected, !halo && className)}
            style={halo ? undefined : style}
            data-ai-brand={brand?.id || 'unknown'}
            aria-hidden="true"
        >
            {mark ? (
                <svg className={styles.mark} viewBox={mark.viewBox} focusable="false">
                    {mark.gradient && (
                        <defs>
                            <linearGradient
                                id={gradientId}
                                x1={mark.gradient.x1}
                                y1={mark.gradient.y1}
                                x2={mark.gradient.x2}
                                y2={mark.gradient.y2}
                            >
                                {mark.gradient.stops.map(stop => (
                                    <stop
                                        key={`${stop.offset}-${stop.color}`}
                                        offset={stop.offset}
                                        stopColor={stop.color}
                                    />
                                ))}
                            </linearGradient>
                        </defs>
                    )}
                    {mark.paths.map((path, index) => (
                        <path
                            key={index}
                            d={path.d}
                            fill={
                                path.fill === 'gradient'
                                    ? `url(#${gradientId})`
                                    : path.fill || 'currentColor'
                            }
                            fillOpacity={path.fillOpacity}
                            fillRule={path.fillRule}
                            clipRule={path.fillRule}
                        />
                    ))}
                </svg>
            ) : (
                // A non-span element so host lists that style "span:first-child" leave it alone.
                <strong className={styles.monogram}>{monogram}</strong>
            )}
            {selected && (
                <span className={styles.badge}>
                    <Check className={styles.badgeIcon} strokeWidth={3.2} />
                </span>
            )}
        </span>
    )

    if (!halo) return avatar

    return (
        <span className={cn(styles.halo, className)} style={style} aria-hidden="true">
            {avatar}
        </span>
    )
}
